
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PaymentHistory } from '@/components/invoices/payments/PaymentHistory';
import { formatCurrency } from '@/lib/utils';

interface InvoiceFinancialCardProps {
  invoice: any;
}

const InvoiceFinancialCard: React.FC<InvoiceFinancialCardProps> = ({ invoice }) => {
  const { data: company } = useQuery({
    queryKey: ['company', invoice?.company_id],
    queryFn: async () => {
      if (!invoice?.company_id) return null;
      
      const { data, error } = await supabase
        .from('companies')
        .select('is_vat_registered, "VAT Rate"')
        .eq('id', invoice.company_id)
        .single();

      if (error) {
        console.error('Error fetching company VAT status:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!invoice?.company_id,
  });

  // Fetch credit applications for this invoice
  const { data: creditApplications } = useQuery({
    queryKey: ['invoiceCreditApplications', invoice?.id],
    queryFn: async () => {
      if (!invoice?.id) return [];
      
      const { data, error } = await supabase
        .from('credit_applications')
        .select(`
          id, 
          amount_applied, 
          application_status,
          applied_at,
          customer_credits(credit_note_number)
        `)
        .eq('invoice_id', invoice.id);

      if (error) {
        console.error('Error fetching credit applications:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!invoice?.id,
  });

  // Only show VAT if company is VAT registered and tax percentage > 0
  const showVat = company?.is_vat_registered && invoice.tax_percentage > 0;
  
  // Use the actual tax percentage from the invoice, which should reflect the company's VAT rate
  const displayVatPercentage = invoice.tax_percentage || company?.["VAT Rate"] || 15;

  // Calculate totals including credits
  const totalCreditsApplied = creditApplications?.reduce(
    (sum, app) => sum + (app.amount_applied || 0), 0
  ) || invoice.credit_applied || 0;
  
  const netAmountDue = (invoice.total_amount || 0) - totalCreditsApplied;
  const balanceAfterPayments = netAmountDue - (invoice.paid_amount || 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Financial Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between">
          <span>Subtotal:</span>
          <span className="whitespace-nowrap font-mono">{formatCurrency(invoice.subtotal || 0)}</span>
        </div>
        {showVat && (
          <div className="flex justify-between">
            <span>VAT ({displayVatPercentage}%):</span>
            <span className="whitespace-nowrap font-mono">{formatCurrency(invoice.tax_amount || 0)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold">
          <span>Total:</span>
          <span className="whitespace-nowrap font-mono">{formatCurrency(invoice.total_amount || 0)}</span>
        </div>
        
        {/* Credits Applied Section */}
        {totalCreditsApplied > 0 && (
          <>
            <div className="border-t pt-2">
              <div className="flex justify-between text-blue-600">
                <span>Credits Applied:</span>
                <span className="whitespace-nowrap font-mono">-{formatCurrency(totalCreditsApplied)}</span>
              </div>
              {/* Show individual credit applications */}
              {creditApplications && creditApplications.length > 0 && (
                <div className="ml-4 mt-1 space-y-1">
                  {creditApplications.map((app: any) => (
                    <div key={app.id} className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        {app.customer_credits?.credit_note_number || 'Credit'} 
                        {app.application_status === 'reserved' && ' (Reserved)'}
                      </span>
                      <span className="whitespace-nowrap font-mono">-{formatCurrency(app.amount_applied)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Net Amount Due:</span>
              <span className="whitespace-nowrap font-mono">{formatCurrency(netAmountDue)}</span>
            </div>
          </>
        )}
        
        {/* Payments Section */}
        {invoice.paid_amount > 0 && (
          <>
            <div className="flex justify-between text-green-600">
              <span>Paid:</span>
              <span className="whitespace-nowrap font-mono">{formatCurrency(invoice.paid_amount || 0)}</span>
            </div>
            <div className="flex justify-between font-semibold">
              <span>Balance:</span>
              <span className="whitespace-nowrap font-mono">{formatCurrency(balanceAfterPayments)}</span>
            </div>
          </>
        )}
        
        {/* Show balance due if no payments but credits applied */}
        {invoice.paid_amount <= 0 && totalCreditsApplied <= 0 && (
          <div className="border-t pt-2">
            <div className="flex justify-between font-bold text-lg">
              <span>Amount Due:</span>
              <span className="whitespace-nowrap font-mono">{formatCurrency(invoice.total_amount || 0)}</span>
            </div>
          </div>
        )}

        {/* Payment History */}
        <PaymentHistory 
          invoice={{
            id: invoice.id,
            invoice_number: invoice.invoice_number,
            total_amount: invoice.total_amount || 0,
            paid_amount: invoice.paid_amount || 0,
            customer_id: invoice.customer_id,
            estimate_id: invoice.estimate_id
          }} 
        />
      </CardContent>
    </Card>
  );
};

export default InvoiceFinancialCard;
